/**
 * App-side wiring for `@heidi/dictate/data` (D1, ARCFE-1084).
 *
 * Three things cross the seam. The provider-aware authentication verdict
 * reads `@heidi/auth/state` and is no longer one of them:
 *
 * - `loadT11nHttp` — a LOADER for the app-built client for the standalone
 *   **transcription service**. Deliberately NOT resolved from the
 *   `@heidi/api/client` registry: that registry holds the SCRIBE client family,
 *   and this instance points at a REGION-RESOLVED transcription host
 *   (`getTranscriptionBaseUrl` — AWS/Azure subdomain substitution, on-prem
 *   origin, PR-preview ingress) with its own `X-Country` header, its own
 *   workspace-id header and the desktop confirmed-offline short-circuit.
 *   Putting it in the scribe registry would let "resolve a scribe client"
 *   return something that is not one. Same reasoning as the EHR `inboxHttp`
 *   precedent, which the templates seam already cites.
 *
 *   IT IS A DYNAMIC IMPORT, AND THAT IS LOAD-BEARING — do not "simplify" it to a
 *   static import. This module is imported for effect by `_app`, so a static
 *   import would evaluate `t11nClient.ts`'s cone at `_app` module scope. That
 *   cone reaches `@/hooks/query/useQueryUser`, the sync auth-token helper and
 *   `@heidi/workspace/data`, which in turn reach the Rematch store — an async
 *   module under rspack/webpack. Pulling it that early is exactly what produced
 *   `ReferenceError: Cannot access 'v' before initialization` during "Collecting
 *   page data" on the integrations carve (ARCFE-1016), on a page with no
 *   connection to that domain. No local gate sees it: tsc, jest and lint are all
 *   blind to module-initialisation order in a bundled chunk, so only a
 *   production build catches a regression here.
 *
 *   Deferring also restores the pre-carve timing: before the move, the five
 *   log-CRUD fns lived in `t11nClient.ts` itself, so its module scope was only
 *   evaluated when something reached for it.
 *
 * - `useAuthToken` — the app's reactive token hook, which is literally
 *   `useSelector((state: RootState) => state.auth.token)`. Injected rather than
 *   imported because no package `/data` zone may reach `@/store` or
 *   `react-redux` (severance cohort S2). Safe to import statically: `_app`
 *   already pulls this module via `workspace-data-wiring`.
 *
 * - `getQueryClient` — the app's singleton React Query client, as a GETTER and
 *   never as the instance, which is the ruled shape for every package that
 *   needs the app cache (task 2.7 of `openspec/changes/monorepo-ui-endgame`).
 *   Resolving per call is what lets a module-scope consumer in the package be
 *   evaluated before this wiring runs without racing client construction. It is
 *   wired unconditionally even though the package has no consumer yet: the
 *   crossing is declared where it can be seen, and the key-list assertion in
 *   this module's test is what stops the wiring silently going absent when the
 *   first consumer arrives. Safe to import statically, unlike `loadT11nHttp`
 *   above: `_app` already pulls `@/utils/reactQueryClient` at module scope (it
 *   builds the client the provider renders), so nothing new joins that cone.
 *
 * WHAT IS NOT HERE, so the omission reads as a measurement. The query client
 * does NOT cross as an instance field (`queryClient: QueryClient`), the shape
 * five other packages carry. No carved module needs one: both delete mutations
 * call `useQueryClient()` inside the hook body exactly as they did before the
 * move, and `invalidateDictationLogsStats` takes its `QueryClient` as an
 * explicit parameter (ARCFE-1067).
 *
 * - `useSupportedLanguages` / `loadLocaleMessages` — the two crossings the
 *   verbal-commands tab needed when it moved behind the door, and the two on
 *   this seam that are NOT a store or cache coupling: they are app-owned
 *   CATALOGUES.
 *
 *   `useQuerySupportedLanguages` is the language vocabulary. Its base query's
 *   core does live in `@heidi/session/data`, so this could have been a door
 *   read — and is not, deliberately: `@heidi/dictate` reaching
 *   `@heidi/session/data` is a new cross-domain edge, and the core takes
 *   `isAuthenticated` as a parameter, so the package would have to answer
 *   "is the user authenticated" a second time. The app hook already resolves
 *   that verdict. Safe to import statically — `_app` pulls
 *   `session-data-wiring` above this line, so nothing new joins its cone.
 *
 *   `loadLocaleMessages` is one locale's ICU catalog. The bundles under
 *   `src/locales/**` are written by the Crowdin pipeline and code-split by
 *   `next`, so the loader table is app-owned by construction; only the fetch
 *   crosses, and the locale ALIAS resolution stays in-package over
 *   `@heidi/utils/i18n` (which is all `@/utils/i18n/supportedLocales` ever
 *   was). Per-call and async, which is what keeps the dynamic `import()`
 *   behind each loader doing its job.
 *
 * Imported at module scope in `_app.tsx` AFTER `workspace-data-wiring`, whose
 * store this hook reads.
 */
import {
  IS_DICTATION_LOGS_BACKEND_ENABLED,
  isDictateUserFeedbackIncreasedCadenceEnabled,
  isDictationContextEnabled,
  isDictationLogOptOutEnabled,
} from '@/app-shell/flags/dictate-flags';
import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import { chronicleSessionIdAtom } from '@/atoms/chronicle/session';
import { useIsAnyDictationActive } from '@/atoms/dictationAtom';
import {
  getDictationHotkey,
  useDictationHotkey,
} from '@/composition/hosts/dictation/hooks/useDictationHotkey';
import {
  isDictationEnabled,
  useIsDictationEnabled,
} from '@/composition/hosts/dictation/hooks/useIsDictationEnabled';
import { resolveAnonymousTranscriptionFallbackRegion } from '@/composition/hosts/dictation/utils/resolveAnonymousTranscriptionFallbackRegion';
import { useQuerySupportedLanguages } from '@/hooks/query/useQuerySupportedLanguages';
import { useAuthToken } from '@/hooks/useIsAuthenticated';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import { localeMessageLoaders, toIntlMessages } from '@/utils/i18n/localeMessageLoaders';
import { type SupportedLocale } from '@/utils/i18n/supportedLocales';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { getScribeHttpClients } from '@heidi/api/client';
import { userBillingInfoQueryOptions } from '@heidi/commerce/data';
import { configureDictationBubblePorts } from '@heidi/dictate';
import { type DictationContextResponse } from '@heidi/dictate/contracts';
import { configureDictationData } from '@heidi/dictate/data';
import { useIsDictateReengagementEnabled } from '@heidi/dictate/flags';
import { isEditorLive } from '@heidi/editor/core';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import {
  CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY,
  CREATE_NEW_SESSION_MUTATION_KEY,
} from '@heidi/session/contracts';
import { getCurrentUser } from '@heidi/workspace/data';
import { type Editor } from '@tiptap/react';
import { getDefaultStore } from 'jotai';

/**
 * The app's supported-language catalog, named because the seam member is a
 * hook. Bound straight through — the query result carries `data` among its
 * other fields, which is the whole structural port.
 */
const useSupportedLanguagesCatalog = () => useQuerySupportedLanguages();

configureDictationData({
  getQueryClient: () => reactQueryClient,
  /**
   * One locale's ICU catalog, or `null` when that locale has no bundle
   * registered — an ordinary outcome the reader falls back to English on. A
   * rejection stays a rejection; the reader logs it.
   */
  loadLocaleMessages: async (locale) => {
    const loader = localeMessageLoaders[locale as SupportedLocale];
    if (!loader) return null;
    const loaded = await loader();
    return toIntlMessages(loaded.default ?? loaded);
  },
  loadT11nHttp: () =>
    import('@/lib/SessionRecordingProvider/t11nClient').then((m) => m.t11nClient),
  useAuthToken,
  useSupportedLanguages: useSupportedLanguagesCatalog,
});

// Same request as the endpoint hub's `getDictationContext`, on the same
// `scribeHttpV2` singleton, without making this module an importer of the hub.
const DICTATION_CONTEXT_PATH = '/dictation-post-processing/context';

// Every member calls through at call time. The transcription-client reads
// stay dynamic imports, for the same TDZ reason as `loadT11nHttp`.
configureDictationBubblePorts({
  getDictationHotkey: () => getDictationHotkey(),
  useDictationHotkey: () => useDictationHotkey(),
  isDictationEnabled: () => isDictationEnabled(),
  useIsDictationEnabled: () => useIsDictationEnabled(),
  setReadyToRequestMicPermissions: () => {
    getDefaultStore().set(readyToRequestMicPermissionsAtom, true);
  },
  isProductFeedbackAvailable: () => isProductFeedbackAvailable,
  isIncreasedFeedbackCadenceEnabled: () => isDictateUserFeedbackIncreasedCadenceEnabled(),
  resolveIsFreeNonManagedUser: async () => {
    const user = getCurrentUser();
    if (user?.is_managed !== false) return false;
    try {
      const billingInfo = await reactQueryClient.fetchQuery(
        userBillingInfoQueryOptions({
          enabled: true,
          affiliateCode: user.affiliate_signup_code,
        })
      );
      const effectiveTier =
        billingInfo?.third_party_license?.latest_tier ?? billingInfo?.tier;
      return effectiveTier === SubscriptionTier.FREE;
    } catch {
      return false;
    }
  },
  isDictationLogsBackendEnabled: () => IS_DICTATION_LOGS_BACKEND_ENABLED(),
  isDictationContextEnabled: () => isDictationContextEnabled(),
  isDictationLogOptOutEnabled: () => isDictationLogOptOutEnabled(),
  fetchDictationContext: (signal) =>
    getScribeHttpClients().scribeHttpV2.get<DictationContextResponse>(
      DICTATION_CONTEXT_PATH,
      { signal }
    ),
  loadT11nHttpV2: () =>
    import('@/lib/SessionRecordingProvider/t11nClient').then((m) => m.t11nClientV2),
  resolveAnonymousTranscriptionBaseUrl: () =>
    import('@/lib/SessionRecordingProvider/t11nClient').then((m) =>
      m.getTranscriptionBaseUrl(resolveAnonymousTranscriptionFallbackRegion())
    ),
  isChronicleRecording: () => getDefaultStore().get(chronicleSessionIdAtom) !== null,
  isSessionCreateMutationKey: (key) =>
    key === CREATE_NEW_SESSION_MUTATION_KEY ||
    key === CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY,
  useIsAnyDictationActive: () => useIsAnyDictationActive(),
  useIsClinicalRecordingActive: (isTimerRunning) =>
    useIsClinicalRecordingActive(isTimerRunning),
  isEditorLive: (editor, callSite): editor is Editor => isEditorLive(editor, callSite),
  useIsDictateReengagementEnabled: () => useIsDictateReengagementEnabled(),
});
